import React, { useState, useMemo } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import { useApp } from '../../context/AppContext';
import KPICard from '../../components/KPICard/KPICard';
import Card from '../../components/Card/Card';
import { RoleBadge, FlightTypeBadge } from '../../components/Badge/Badge';
import '../../styles/layout.css';
import '../../styles/tables.css';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const HBAR_OPTS = {
  indexAxis: 'y',
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: { grid: { color: '#f0f3f8' }, ticks: { font: { size: 9.5 }, color: '#8faabb' } },
    y: { grid: { display: false }, ticks: { font: { size: 9.5 }, color: '#3d5166' } },
  },
};

const VBAR_OPTS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 9.5 }, color: '#8faabb' } },
    y: { grid: { color: '#f0f3f8' }, ticks: { font: { size: 10 }, color: '#8faabb' } },
  },
};

const DONUT_OPTS = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '72%',
  plugins: {
    legend: { display: false },
    tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${ctx.parsed}` } },
  },
};

const OP_COLORS = [
  '#1246d6', '#f97316', '#16a34a', '#7c3aed', '#db2777',
  '#0891b2', '#ca8a04', '#dc2626', '#059669', '#9333ea',
];

function parseDur(str) {
  if (!str) return 0;
  const [h, m] = str.split(':').map(Number);
  return (h || 0) + (m || 0) / 60;
}

function fmtDur(hours) {
  if (!hours) return '—';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

function filterFlights(D, op) {
  if (!op || op === 'All') return D.flights || [];
  return (D.flights || []).filter((r) => r.op === op);
}

function filterUtil(D, op) {
  if (!op || op === 'All') return D.util || [];
  return (D.util || []).filter((r) => r.op === op);
}

export default function Flights() {
  const { operator, D, OPC } = useApp();
  const [search, setSearch] = useState('');

  const flights  = useMemo(() => filterFlights(D, operator), [D, operator]);
  const utilData = useMemo(() => filterUtil(D, operator), [D, operator]);

  const totalFlights = flights.length;

  const intlCount = useMemo(() => flights.filter((r) => r.intl === true).length, [flights]);
  const intlPct   = totalFlights ? Math.round((intlCount / totalFlights) * 100) : 0;
  const domCount  = totalFlights - intlCount;
  const domPct    = 100 - intlPct;

  const avgDur = useMemo(() => {
    if (!flights.length) return 0;
    return flights.reduce((s, r) => s + parseDur(r.dur), 0) / flights.length;
  }, [flights]);

  const uniqueRoutes = useMemo(() => {
    return new Set(flights.map((r) => `${r.oico}-${r.dico}`)).size;
  }, [flights]);

  // ── Flights by operator (progress bar list) ──────────────────────────────
  const byOperator = useMemo(() => {
    const map = {};
    flights.forEach((r) => { map[r.op] = (map[r.op] || 0) + 1; });
    return Object.entries(map)
      .map(([op, count]) => ({ op, count }))
      .sort((a, b) => b.count - a.count);
  }, [flights]);

  // ── Domestic vs International doughnut ────────────────────────────────────
  const doughnutData = useMemo(() => ({
    labels: ['Domestic', 'International'],
    datasets: [{
      data: [domCount, intlCount],
      backgroundColor: ['#1246d6', '#f97316'],
      borderWidth: 0,
    }],
  }), [domCount, intlCount]);

  // ── Flights by variant (horizontal bar) ───────────────────────────────────
  const variantBarData = useMemo(() => {
    const map = {};
    flights.forEach((r) => { map[r.v || 'Unknown'] = (map[r.v || 'Unknown'] || 0) + 1; });
    const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 8);
    return {
      labels: sorted.map(([v]) => v),
      datasets: [{
        data: sorted.map(([, c]) => c),
        backgroundColor: '#1246d6',
        borderRadius: 3,
      }],
    };
  }, [flights]);

  // ── Top routes by frequency (horizontal bar) ──────────────────────────────
  const topRoutesData = useMemo(() => {
    const map = {};
    flights.forEach((r) => {
      const key = `${r.oico}→${r.dico}`;
      map[key] = (map[key] || 0) + 1;
    });
    const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 8);
    return {
      labels: sorted.map(([k]) => k),
      datasets: [{
        data: sorted.map(([, c]) => c),
        backgroundColor: '#7c3aed',
        borderRadius: 3,
      }],
    };
  }, [flights]);

  // ── Avg stage length by operator (from util.stg) ──────────────────────────
  const stageLenData = useMemo(() => {
    const map = {};
    utilData.forEach((r) => {
      if (!map[r.op]) map[r.op] = { sum: 0, n: 0 };
      map[r.op].sum += parseDur(r.stg);
      map[r.op].n += 1;
    });
    const entries = Object.entries(map)
      .map(([op, { sum, n }]) => ({ op, avg: n ? sum / n : 0 }))
      .sort((a, b) => b.avg - a.avg);
    return {
      labels: entries.map((e) => e.op),
      datasets: [{
        data: entries.map((e) => parseFloat(e.avg.toFixed(2))),
        backgroundColor: entries.map((_, i) => OP_COLORS[i % OP_COLORS.length]),
        borderRadius: 3,
      }],
    };
  }, [utilData]);

  // ── Top routes table (duration) ───────────────────────────────────────────
  const routeTable = useMemo(() => {
    const map = {};
    flights.forEach((r) => {
      const key = `${r.oico}→${r.dico}`;
      if (!map[key]) map[key] = { oico: r.oico, dico: r.dico, ori: r.ori, dst: r.dst, sum: 0, n: 0 };
      map[key].sum += parseDur(r.dur);
      map[key].n += 1;
    });
    return Object.values(map)
      .sort((a, b) => b.n - a.n)
      .slice(0, 10)
      .map((v) => ({
        route:  `${v.oico} → ${v.dico}`,
        ori:    v.ori,
        dst:    v.dst,
        count:  v.n,
        avgDur: v.n ? v.sum / v.n : 0,
      }));
  }, [flights]);

  // ── Flight log (searchable) ────────────────────────────────────────────────
  const logRows = useMemo(() => {
    if (!search.trim()) return flights;
    const q = search.toLowerCase();
    return flights.filter((r) =>
      [r.date, r.op, r.reg, r.v, r.oico, r.dico, r.ori, r.dst, r.role]
        .filter(Boolean).join(' ').toLowerCase().includes(q)
    );
  }, [flights, search]);

  return (
    <div className="page-content">

      {/* KPI Row */}
      <div className="krow krow-4">
        <KPICard label="Total Flights"    value={totalFlights.toLocaleString()} sub="Filtered operations"        accent="kb" />
        <KPICard label="International"    value={intlCount.toLocaleString()}    sub={`${intlPct}% of total`}     accent="ko" />
        <KPICard label="Avg Duration"     value={fmtDur(avgDur)}                sub="Per flight average"          accent="kt" />
        <KPICard label="Unique Routes"    value={uniqueRoutes}                  sub="Origin–destination pairs"    accent="kg" />
      </div>

      {/* Row 1: Operator list + Dom/Intl split + Variant bar */}
      <div className="g3">
        {/* Left: Operator breakdown */}
        <Card title="Flights by Operator" icon="🏢" iconClass="ib">
          {byOperator.length === 0 ? (
            <div className="empty">No flight data available.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {byOperator.map(({ op, count }, i) => {
                const pct   = totalFlights ? Math.round((count / totalFlights) * 100) : 0;
                const color = OPC?.[op] || OP_COLORS[i % OP_COLORS.length];
                return (
                  <div key={op}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 11.5 }}>
                      <span style={{ color: 'var(--tx)', fontWeight: 500 }}>{op}</span>
                      <span style={{ color: 'var(--tx3)', fontFamily: 'Space Mono, monospace', fontSize: 11 }}>
                        {count} · {pct}%
                      </span>
                    </div>
                    <div className="pb">
                      <div className="pbf" style={{ width: `${pct}%`, background: color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Dom/Intl donut */}
          <Card title="Domestic vs International" icon="🌍" iconClass="it">
            <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
              <div style={{ position: 'relative', width: 110, height: 110, flexShrink: 0 }}>
                <Doughnut data={doughnutData} options={DONUT_OPTS} />
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontFamily: 'Space Mono, monospace', fontWeight: 700, fontSize: 18, color: 'var(--tx)' }}>{totalFlights}</span>
                  <span style={{ fontSize: 9, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Flights</span>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
                <div>
                  <div style={{ fontFamily: 'Space Mono, monospace', fontSize: 20, fontWeight: 700, color: '#1246d6' }}>{domPct}%</div>
                  <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>Domestic — {domCount}</div>
                </div>
                <div>
                  <div style={{ fontFamily: 'Space Mono, monospace', fontSize: 20, fontWeight: 700, color: '#f97316' }}>{intlPct}%</div>
                  <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>International — {intlCount}</div>
                </div>
              </div>
            </div>
          </Card>

          {/* Flights by variant */}
          <Card title="Flights by Aircraft Type" icon="✈" iconClass="ib">
            <div className="cwxs">
              <Bar data={variantBarData} options={HBAR_OPTS} />
            </div>
          </Card>
        </div>
      </div>

      {/* Row 2: Top routes bar + Stage length + Route table */}
      <div className="g2">
        <Card title="Top Routes by Frequency" icon="🗺️" iconClass="ip">
          <div className="cwsm">
            <Bar data={topRoutesData} options={HBAR_OPTS} />
          </div>
        </Card>

        <Card title="Avg Stage Length by Operator" icon="📏" iconClass="it">
          <div className="cwsm">
            <Bar data={stageLenData} options={VBAR_OPTS} />
          </div>
        </Card>
      </div>

      {/* Route duration table */}
      <Card title="Top Routes by Duration" subtitle="Top 10 by flight count" icon="⏱️" iconClass="ib" noPad>
        <div className="ts full">
          <table>
            <thead>
              <tr>
                <th>Route (ICAO)</th>
                <th>Origin</th>
                <th>Destination</th>
                <th>Flights</th>
                <th>Avg Duration</th>
              </tr>
            </thead>
            <tbody>
              {routeTable.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', color: 'var(--tx3)', padding: '24px 14px' }}>
                    No flight data available.
                  </td>
                </tr>
              ) : (
                routeTable.map((r) => (
                  <tr key={r.route}>
                    <td>
                      <div className="route">
                        <span className="rc">{r.route.split(' → ')[0]}</span>
                        <span className="ra">→</span>
                        <span className="rc">{r.route.split(' → ')[1]}</span>
                      </div>
                    </td>
                    <td style={{ color: 'var(--tx2)', fontSize: 11 }}>{r.ori}</td>
                    <td style={{ color: 'var(--tx2)', fontSize: 11 }}>{r.dst}</td>
                    <td style={{ fontFamily: 'Space Mono, monospace', fontSize: 11 }}>{r.count}</td>
                    <td style={{ fontWeight: 600, fontSize: 11.5, color: r.avgDur < 2 ? '#16a34a' : r.avgDur < 6 ? '#d97706' : '#dc2626' }}>
                      {fmtDur(r.avgDur)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Flight Log */}
      <Card
        title="Flight Log"
        icon="📋"
        iconClass="ib"
        subtitle={`${logRows.length} record${logRows.length !== 1 ? 's' : ''}`}
        noPad
        headerRight={
          <div className="sw">
            <span className="si">🔍</span>
            <input
              className="sinp"
              placeholder="Search flights…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        }
      >
        <div className="ts full">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Operator</th>
                <th>Reg</th>
                <th>Variant</th>
                <th>Origin</th>
                <th>Destination</th>
                <th>Duration</th>
                <th>Role</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              {logRows.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', color: 'var(--tx3)', padding: '24px 14px' }}>
                    No flights match the current filters.
                  </td>
                </tr>
              ) : (
                logRows.slice(0, 300).map((r, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'Space Mono, monospace', fontSize: 11 }}>{r.date}</td>
                    <td>{r.op}</td>
                    <td><strong>{r.reg}</strong></td>
                    <td>{r.v || '—'}</td>
                    <td>
                      <div style={{ fontSize: 11 }}>
                        <span className="rc">{r.oico}</span>{' '}
                        <span style={{ color: 'var(--tx3)', fontSize: 10 }}>{r.ori}</span>
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: 11 }}>
                        <span className="rc">{r.dico}</span>{' '}
                        <span style={{ color: 'var(--tx3)', fontSize: 10 }}>{r.dst}</span>
                      </div>
                    </td>
                    <td style={{ fontFamily: 'Space Mono, monospace', fontSize: 11 }}>{r.dur || '—'}</td>
                    <td><RoleBadge role={r.role} /></td>
                    <td><FlightTypeBadge intl={r.intl === true} /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

    </div>
  );
}
